/* eslint-disable @typescript-eslint/no-explicit-any */
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import PageSkeleton from '../../components/common/page-skeleton';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useEffect, useState } from 'react';

import { projectsService, type Project } from '../../services/projects.service';
import { partnersService } from '../../services/partners.service';
import { extractErrorMessage } from '../../utils/error-handler';
import { useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import { AuditInfoCard } from '../../components/common/audit-info-card';
import ScreenHeader from '../../components/common/screen-header';

const ProjectView = () => {
  const { id, isReady, navigateToEdit, navigateToList } = useSecureNavigation({
    entity: 'apps',
    basePath: '/projects',
  });

  const canUpdate = useCanUpdate(JOB_NAMES.APPS);
  const canDelete = useCanDelete(JOB_NAMES.APPS);

  const [data, setData] = useState<Project | null>(null);
  const [partnerName, setPartnerName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  // Falls back to the folder icon when the project has no logo or it fails to load.
  const [logoBroken, setLogoBroken] = useState(false);
  // Full-size logo preview dialog.
  const [logoPreviewOpen, setLogoPreviewOpen] = useState(false);

  useBreadcrumbTitle(data?.name);

  useEffect(() => {
    if (!isReady || !id) {
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const res = await projectsService.getById(id);
        setData(res);
        // Resolve the owning partner's display name (best-effort; blank if absent).
        if (res.partnerMasterId) {
          try {
            const partners = await partnersService.getAllForDropdown();
            setPartnerName(partners.find((p) => p._id === res.partnerMasterId)?.name || '');
          } catch { setPartnerName(''); }
        } else {
          setPartnerName('');
        }
      } catch (err: any) {
        setError(extractErrorMessage(err, 'Failed to fetch project'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, id]);

  const handleEdit = () => {
    if (id) navigateToEdit(id);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!id) return;
    try {
      await projectsService.delete(id);
      setDeleteDialogOpen(false);
      navigateToList();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to delete project'));
    }
  };

  if (loading) {
    return (
      <PageSkeleton />
    );
  }

  if (error || !data) {
    return (
      <div className="p-4">
        <Message severity="error" text={error || 'Project not found'} className="mb-4 w-full" />
        <Button
          label="Back to Projects"
          icon="pi pi-arrow-left"
          severity="secondary"
          outlined
          onClick={() => navigateToList()}
        />
      </div>
    );
  }

  const deleteDialogFooter = (
    <div className="flex justify-end gap-2">
      <Button label="Cancel" severity="secondary" outlined onClick={() => setDeleteDialogOpen(false)} />
      <Button label="Delete" severity="danger" onClick={handleDeleteConfirm} />
    </div>
  );

  return (
    <div className="p-3" data-testid="SCR-Project-View">
      <ScreenHeader icon="pi pi-folder" title="Project Details" onBack={() => navigateToList()} />

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-2">
        <div className="flex items-start gap-2.5">
          <div className="relative group w-12 h-12 rounded bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {(data as any).imageUrl && !logoBroken ? (
              <>
                <img
                  src={(data as any).imageUrl}
                  alt={data.name}
                  className="w-full h-full object-cover"
                  onError={() => setLogoBroken(true)}
                />
                <button
                  type="button"
                  aria-label="View full logo"
                  onClick={() => setLogoPreviewOpen(true)}
                  className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/40 transition-colors cursor-pointer"
                  data-pr-tooltip="View full image"
                >
                  <i className="pi pi-eye text-white opacity-0 group-hover:opacity-100 transition-opacity" style={{ fontSize: '1rem' }} />
                </button>
              </>
            ) : (
              <i className="pi pi-th-large text-blue-600 dark:text-blue-400" style={{ fontSize: '1.25rem' }} />
            )}
          </div>

          <div>
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h2 className="text-xl font-bold text-ink">{data.name}</h2>
              {data.appCode && (
                <Tag value={data.appCode} severity="info" className="text-xs px-1.5 py-0.5" />
              )}
              {(data as any).isDelete || data.isDeleted ? (
                <Tag value="Deleted" severity="danger" className="text-xs px-1.5 py-0.5" />
              ) : (
                <Tag value="Active" severity="success" className="text-xs px-1.5 py-0.5" />
              )}
            </div>
            <p className="text-xs text-ink-muted">
              Project Configuration
            </p>
          </div>
        </div>

        {!((data as any).isDelete || data.isDeleted) && (
          <div className="flex gap-1.5 sm:self-center">
            {canUpdate && (
              <Button label="Edit" icon="pi pi-pencil" onClick={handleEdit} size="small" />
            )}
            {canDelete && (
              <Button label="Delete" icon="pi pi-trash" severity="danger" onClick={handleDeleteClick} size="small" />
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
        <div className="ds-card p-4">
          <h2 className="text-sm font-semibold text-ink mb-3">Project Information</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
            <div>
              <label className="text-xs text-ink-muted block mb-0.5">Name</label>
              <p className="text-sm font-medium text-ink">{data.name || '-'}</p>
            </div>
            <div>
              <label className="text-xs text-ink-muted block mb-0.5">Project Code</label>
              <p className="text-sm font-mono text-ink">{data.appCode || '-'}</p>
            </div>
            <div>
              <label className="text-xs text-ink-muted block mb-0.5">Partner</label>
              <p className="text-sm text-ink">{partnerName || '-'}</p>
            </div>
            <div>
              <label className="text-xs text-ink-muted block mb-0.5">Start Date</label>
              <p className="text-sm text-ink">
                {data.startAt ? new Date(data.startAt).toLocaleDateString() : '-'}
              </p>
            </div>
            <div>
              <label className="text-xs text-ink-muted block mb-0.5">Payment</label>
              <Tag
                value={data.paymentDone ? 'Paid' : 'Not paid'}
                severity={data.paymentDone ? 'success' : 'warning'}
                className="text-xs px-1.5 py-0.5"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-ink-muted block mb-0.5">Description</label>
              <p className="text-sm text-gray-700 dark:text-gray-300">{data.description || 'No description provided.'}</p>
            </div>
          </div>
        </div>

        {/* Data Source & Settings Summary */}
        <div className="ds-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-ink flex items-center gap-2">
              <i className="pi pi-database text-primary-500" />
              Data Source & Settings
            </h2>
            <div className="flex items-center gap-1.5">
              <Tag
                value={data.projectSettings?.dataSource?.status || 'UNCONFIGURED'}
                severity={
                  data.projectSettings?.dataSource?.status === 'ACTIVE'
                    ? 'success'
                    : data.projectSettings?.dataSource?.status === 'ERROR'
                    ? 'danger'
                    : 'secondary'
                }
              />
              {data.projectSettings?.profileGeneration && (
                <Tag value={`Gen ${data.projectSettings.profileGeneration}`} severity="warning" />
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-2 text-xs">
            <div>
              <span className="text-ink-muted block">Connector:</span>
              <span className="font-medium text-ink capitalize">
                {data.projectSettings?.dataSource?.connector || 'internal (control DB)'}
              </span>
            </div>

            <div>
              <span className="text-ink-muted block">Direction:</span>
              <span className="font-medium text-ink capitalize">
                {data.projectSettings?.dataSource?.direction || 'inbound'}
              </span>
            </div>

            <div>
              <span className="text-ink-muted block">Custody:</span>
              <span className="font-medium text-ink capitalize">
                {data.projectSettings?.dataSource?.custody || 'platform'}
              </span>
            </div>

            <div>
              <span className="text-ink-muted block">Database Name:</span>
              <span className="font-mono text-ink">
                {data.projectSettings?.dataSource?.mongo?.dbName || '-'}
              </span>
            </div>

            <div>
              <span className="text-ink-muted block">Pool Sizing:</span>
              <span className="font-medium text-ink">
                {data.projectSettings?.dataSource?.mongo?.minPoolSize ?? 1} -{' '}
                {data.projectSettings?.dataSource?.mongo?.maxPoolSize ?? 10} conns
              </span>
            </div>

            <div>
              <span className="text-ink-muted block">Break-Glass:</span>
              <span className="font-medium text-ink capitalize">
                {data.projectSettings?.adminAccess?.breakGlass || 'disabled'}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2">
        <AuditInfoCard
          createdAt={data.createdAt}
          updatedAt={data.updatedAt}
          createdBy={data.createdBy}
          updatedBy={data.updatedBy}
        />
      </div>

      <Dialog
        visible={deleteDialogOpen}
        onHide={() => setDeleteDialogOpen(false)}
        header="Delete Project"
        footer={deleteDialogFooter}
        style={{ width: '400px' }}
        modal
        dismissableMask
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <i className="pi pi-exclamation-triangle text-red-600" style={{ fontSize: '1.25rem' }} />
          </div>
          <div>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Are you sure you want to delete <strong>{data.name}</strong>?
            </p>
            <p className="text-xs text-ink-muted mt-2">
              This action cannot be undone.
            </p>
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={logoPreviewOpen}
        onHide={() => setLogoPreviewOpen(false)}
        header={data.name}
        style={{ width: 'auto', maxWidth: '90vw' }}
        modal
        dismissableMask
      >
        {(data as any).imageUrl && (
          <img
            src={(data as any).imageUrl}
            alt={data.name}
            style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', display: 'block', margin: '0 auto' }}
          />
        )}
      </Dialog>
    </div>
  );
};

export default ProjectView;
